import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { School, User, Phone, Mail, MapPin, Calendar, FileText, CheckCircle, AlertTriangle, Heart, ArrowLeft } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GRAUS_PARENTESCO = [
  { id: "pai", label: "Pai" },
  { id: "mae", label: "Mãe" },
  { id: "avo", label: "Avô/Avó" },
  { id: "tio", label: "Tio/Tia" },
  { id: "responsavel", label: "Responsável Legal" },
  { id: "outro", label: "Outro" }
];

export default function PreMatricula() {
  const { codigo } = useParams();
  const navigate = useNavigate();
  const [escola, setEscola] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState(null);

  const [formData, setFormData] = useState({
    aluno_nome: "",
    aluno_data_nascimento: "",
    aluno_cpf: "",
    aluno_endereco: "",
    aluno_telefone: "",
    observacoes_medicas: "",
    responsavel: {
      nome: "",
      telefone: "",
      email: "",
      grau_parentesco: ""
    },
    contrato_aceito: false,
    assinatura_nome: ""
  });

  // Calcula se o aluno é maior de idade
  const calcularIdade = (dataNascimento) => {
    if (!dataNascimento) return 0;
    const hoje = new Date();
    const nascimento = new Date(dataNascimento);
    let idade = hoje.getFullYear() - nascimento.getFullYear();
    const mesAtual = hoje.getMonth();
    const mesNascimento = nascimento.getMonth();
    if (mesAtual < mesNascimento || (mesAtual === mesNascimento && hoje.getDate() < nascimento.getDate())) {
      idade--;
    }
    return idade;
  };

  const idade = calcularIdade(formData.aluno_data_nascimento);
  const isMaiorDeIdade = idade >= 18;

  useEffect(() => {
    fetchEscola();
  }, [codigo]);

  const fetchEscola = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/pre-matricula/escola/${codigo}`);
      if (response.data.encontrada && response.data.ativa) {
        setEscola(response.data);
      } else if (response.data.encontrada && !response.data.ativa) {
        setErro(response.data.mensagem || "Esta escola não está ativa no momento.");
      } else {
        setErro("Escola não encontrada com este código.");
      }
    } catch (error) {
      setErro("Erro ao buscar dados da escola. Verifique o código e tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.contrato_aceito) {
      toast.error("Você precisa aceitar os termos do contrato para continuar.");
      return;
    }

    if (!formData.assinatura_nome.trim()) {
      toast.error("Por favor, digite seu nome completo para assinar o contrato.");
      return;
    }

    // Validação condicional: se menor de idade, precisa dos dados do responsável
    if (!isMaiorDeIdade) {
      if (!formData.responsavel.nome || !formData.responsavel.telefone || !formData.responsavel.grau_parentesco) {
        toast.error("Para menores de 18 anos, os dados do responsável são obrigatórios.");
        return;
      }
    } else {
      // Se maior de idade, precisa do telefone do próprio aluno
      if (!formData.aluno_telefone) {
        toast.error("Por favor, informe seu telefone de contato.");
        return;
      }
    }

    // Preparar dados para envio
    const dadosEnvio = {
      ...formData,
      // Se maior de idade, enviar responsável vazio ou com dados do próprio aluno
      responsavel: isMaiorDeIdade ? {
        nome: formData.aluno_nome,
        telefone: formData.aluno_telefone,
        email: formData.responsavel.email || "",
        grau_parentesco: "proprio"
      } : formData.responsavel
    };

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/pre-matricula/${codigo}`, dadosEnvio);
      if (response.data.success) {
        setEnviado(true);
        toast.success("Pré-matrícula enviada com sucesso!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar pré-matrícula");
    } finally {
      setSubmitting(false);
    }
  };

  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return `(${numbers}`;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-400 via-blue-500 to-purple-600 flex items-center justify-center p-4">
        <div className="text-white text-xl">Carregando...</div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-400 via-blue-500 to-purple-600 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertTriangle className="w-16 h-16 mx-auto text-amber-500 mb-4" />
            <CardTitle className="text-xl">Ops!</CardTitle>
            <CardDescription>{erro}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate("/")} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar ao Início
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (enviado) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-400 via-blue-500 to-purple-600 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <CardTitle className="text-xl text-green-700">Pré-Matrícula Enviada!</CardTitle>
            <CardDescription>
              Sua pré-matrícula foi enviada com sucesso para <strong>{escola?.nome_escola}</strong>.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-600">
              A escola entrará em contato em breve para finalizar o processo de matrícula.
            </p>
            {escola?.telefone && (
              <p className="text-sm text-gray-500">
                Dúvidas? Entre em contato: <strong>{escola.telefone}</strong>
              </p>
            )}
            <Button onClick={() => navigate("/")} variant="outline" className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar ao Início
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-400 via-blue-500 to-purple-600 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <Card className="mb-6">
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <School className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">{escola?.nome_escola}</CardTitle>
            <CardDescription>Formulário de Pré-Matrícula</CardDescription>
          </CardHeader>
        </Card>

        {/* Formulário */}
        <form onSubmit={handleSubmit}>
          {/* Dados do Aluno */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Dados do Aluno
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="aluno_nome">Nome Completo *</Label>
                <Input
                  id="aluno_nome"
                  data-testid="aluno-nome-input"
                  value={formData.aluno_nome}
                  onChange={(e) => setFormData({ ...formData, aluno_nome: e.target.value })}
                  placeholder="Nome completo do aluno"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="aluno_data_nascimento">Data de Nascimento *</Label>
                  <Input
                    id="aluno_data_nascimento"
                    data-testid="aluno-nascimento-input"
                    type="date"
                    value={formData.aluno_data_nascimento}
                    onChange={(e) => setFormData({ ...formData, aluno_data_nascimento: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="aluno_cpf">CPF</Label>
                  <Input
                    id="aluno_cpf"
                    data-testid="aluno-cpf-input"
                    value={formData.aluno_cpf}
                    onChange={(e) => setFormData({ ...formData, aluno_cpf: formatCPF(e.target.value) })}
                    placeholder="000.000.000-00"
                    maxLength={14}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="aluno_endereco">Endereço</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <Input
                    id="aluno_endereco"
                    data-testid="aluno-endereco-input"
                    className="pl-10"
                    value={formData.aluno_endereco}
                    onChange={(e) => setFormData({ ...formData, aluno_endereco: e.target.value })}
                    placeholder="Rua, número, bairro, cidade"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="observacoes_medicas">Observações Médicas / Alergias</Label>
                <Textarea
                  id="observacoes_medicas"
                  data-testid="observacoes-input"
                  value={formData.observacoes_medicas}
                  onChange={(e) => setFormData({ ...formData, observacoes_medicas: e.target.value })}
                  placeholder="Informe alergias, condições médicas ou outras observações importantes..."
                  rows={3}
                />
              </div>

              {/* Mostrar idade calculada */}
              {formData.aluno_data_nascimento && (
                <Alert className={isMaiorDeIdade ? "bg-blue-50 border-blue-200" : "bg-gray-50"}>
                  <AlertDescription className="text-sm">
                    {isMaiorDeIdade ? (
                      <span className="text-blue-700">
                        <strong>Aluno maior de idade ({idade} anos)</strong> - Os dados do responsável são opcionais.
                      </span>
                    ) : (
                      <span className="text-gray-600">
                        Idade do aluno: <strong>{idade} anos</strong> - Dados do responsável obrigatórios.
                      </span>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {/* Telefone do aluno (obrigatório se maior de idade) */}
              {isMaiorDeIdade && (
                <div className="space-y-2">
                  <Label htmlFor="aluno_telefone">Seu Telefone/WhatsApp *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                    <Input
                      id="aluno_telefone"
                      data-testid="aluno-telefone-input"
                      className="pl-10"
                      value={formData.aluno_telefone}
                      onChange={(e) => setFormData({ ...formData, aluno_telefone: formatPhone(e.target.value) })}
                      placeholder="(00) 00000-0000"
                      maxLength={15}
                      required
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Dados do Responsável - Apenas para menores de idade */}
          {!isMaiorDeIdade && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Heart className="w-5 h-5 text-pink-600" />
                Dados do Responsável
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="resp_nome">Nome Completo *</Label>
                <Input
                  id="resp_nome"
                  data-testid="responsavel-nome-input"
                  value={formData.responsavel.nome}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    responsavel: { ...formData.responsavel, nome: e.target.value } 
                  })}
                  placeholder="Nome completo do responsável"
                  required={!isMaiorDeIdade}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="resp_telefone">Telefone/WhatsApp *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                    <Input
                      id="resp_telefone"
                      data-testid="responsavel-telefone-input"
                      className="pl-10"
                      value={formData.responsavel.telefone}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        responsavel: { ...formData.responsavel, telefone: formatPhone(e.target.value) } 
                      })}
                      placeholder="(00) 00000-0000"
                      maxLength={15}
                      required={!isMaiorDeIdade}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="resp_email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                    <Input
                      id="resp_email"
                      data-testid="responsavel-email-input"
                      type="email"
                      className="pl-10"
                      value={formData.responsavel.email}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        responsavel: { ...formData.responsavel, email: e.target.value } 
                      })}
                      placeholder="email@exemplo.com"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="resp_parentesco">Grau de Parentesco *</Label>
                <Select
                  value={formData.responsavel.grau_parentesco}
                  onValueChange={(value) => setFormData({ 
                    ...formData, 
                    responsavel: { ...formData.responsavel, grau_parentesco: value } 
                  })}
                  required={!isMaiorDeIdade}
                >
                  <SelectTrigger data-testid="responsavel-parentesco-select">
                    <SelectValue placeholder="Selecione o grau de parentesco" />
                  </SelectTrigger>
                  <SelectContent>
                    {GRAUS_PARENTESCO.map((grau) => (
                      <SelectItem key={grau.id} value={grau.id}>
                        {grau.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Contrato */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                Termo de Matrícula
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="bg-gray-50">
                <AlertDescription className="text-sm text-gray-600">
                  <p className="mb-2"><strong>TERMO DE MATRÍCULA E AUTORIZAÇÃO</strong></p>
                  <p className="mb-2">
                    Ao enviar esta pré-matrícula, {isMaiorDeIdade ? "o aluno" : "o responsável"} declara estar ciente e de acordo com as seguintes condições:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>O aluno está apto para a prática de atividades esportivas;</li>
                    <li>Autorizo o uso de imagem do aluno para fins pedagógicos e divulgação da escola;</li>
                    <li>Comprometo-me a manter os dados cadastrais atualizados;</li>
                    <li>Comprometo-me com o pagamento das mensalidades nas datas acordadas;</li>
                    <li>A escola não se responsabiliza por objetos pessoais perdidos ou danificados;</li>
                    <li>Em caso de desistência, comunicarei a escola com antecedência mínima de 30 dias.</li>
                  </ul>
                </AlertDescription>
              </Alert>

              <div className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg border border-green-200">
                <Checkbox
                  id="contrato_aceito"
                  data-testid="contrato-checkbox"
                  checked={formData.contrato_aceito}
                  onCheckedChange={(checked) => setFormData({ ...formData, contrato_aceito: checked })}
                />
                <label htmlFor="contrato_aceito" className="text-sm cursor-pointer">
                  <strong>Li e concordo</strong> com todos os termos e condições acima descritos.
                </label>
              </div>

              <div className="space-y-2">
                <Label htmlFor="assinatura_nome">Assinatura Digital (Nome Completo) *</Label>
                <Input
                  id="assinatura_nome"
                  data-testid="assinatura-input"
                  value={formData.assinatura_nome}
                  onChange={(e) => setFormData({ ...formData, assinatura_nome: e.target.value })}
                  placeholder="Digite seu nome completo como assinatura"
                  className="font-signature text-lg"
                  required
                />
                <p className="text-xs text-gray-500">
                  Ao digitar seu nome, você confirma que leu e concorda com os termos.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Botão de Envio */}
          <Button 
            type="submit" 
            className="w-full bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-lg py-6"
            disabled={submitting || !formData.contrato_aceito}
            data-testid="submit-prematricula"
          >
            {submitting ? "Enviando..." : "Enviar Pré-Matrícula"}
          </Button>

          <p className="text-center text-white text-sm mt-4 opacity-80">
            Após o envio, a escola entrará em contato para finalizar a matrícula.
          </p>
        </form>
      </div>
    </div>
  );
}
